/**
 * VAULT domain state — DFY Producer Vault browsing, favourites and downloads.
 *
 * SIMULATED. Pack contents are fixtures bundled into the JS; "downloading" a pack
 * writes a text manifest client-side. Favourites, downloads and recents are
 * in-memory and reset on reload.
 *
 * Access is gated on the `dfyVault` entitlement, which is client-side and
 * therefore advisory — the real endpoints must enforce it server-side.
 */
import { create } from 'zustand';
import { vaultApi } from '../api/vaultApi';
import type { VaultActivity, VaultDownload, VaultPack, VaultRecentEntry } from '../types/domain';

/** Which Vault tab is showing. */
export type VaultTab = 'overview' | 'favorites' | 'downloads' | 'recent';

/** Which resource tab is showing inside an open pack. */
export type VaultResourceTab =
  'overview' | 'prompts' | 'presets' | 'structures' | 'hooks' | 'metadata' | 'marketing';

interface VaultState {
  tab: VaultTab;
  search: string;
  categoryFilter: string;
  /** Pack open in the detail drawer, or null. */
  openPackId: string | null;
  resourceTab: VaultResourceTab;
  favoritePacks: string[];
  /** Favourited individual items (prompts, presets, hooks…) across all packs. */
  favoriteItems: string[];
  downloads: VaultDownload[];
  /** Recently opened pack ids, most recent first. */
  recentPacks: string[];
  recentPrompts: VaultRecentEntry[];
  recentPresets: VaultRecentEntry[];

  hydrateActivity: (activity: VaultActivity) => void;
  setTab: (tab: VaultTab) => void;
  setSearch: (search: string) => void;
  setCategoryFilter: (category: string) => void;
  openPack: (packId: string) => Promise<void>;
  closePack: () => void;
  setResourceTab: (tab: VaultResourceTab) => void;
  togglePackFavorite: (packId: string) => Promise<void>;
  toggleItemFavorite: (itemId: string) => Promise<void>;
  /** Records a pack download. The file itself is built by lib/download.ts. */
  recordDownload: (pack: VaultPack) => Promise<void>;
  /** Notes a prompt loaded into Create. */
  noteRecentPrompt: (packId: string, promptId: string, title: string) => Promise<void>;
  /** Notes a preset loaded into Create. */
  noteRecentPreset: (packId: string, presetId: string, name: string) => Promise<void>;
  loadActivity: () => Promise<void>;
  reset: () => void;
}

const INITIAL = {
  tab: 'overview' as VaultTab,
  search: '',
  categoryFilter: 'All',
  openPackId: null,
  resourceTab: 'overview' as VaultResourceTab,
  favoritePacks: [] as string[],
  favoriteItems: [] as string[],
  downloads: [] as VaultDownload[],
  recentPacks: [] as string[],
  recentPrompts: [] as VaultRecentEntry[],
  recentPresets: [] as VaultRecentEntry[],
};

function applyActivity(set: (partial: Partial<VaultState>) => void, activity: VaultActivity): void {
  set({
    favoritePacks: activity.favoritePacks,
    favoriteItems: activity.favoriteItems,
    downloads: activity.downloads,
    recentPacks: activity.recentPacks,
    recentPrompts: activity.recentPrompts,
    recentPresets: activity.recentPresets,
  });
}

export const useVaultStore = create<VaultState>((set, get) => ({
  ...INITIAL,

  hydrateActivity(activity) {
    applyActivity(set, activity);
  },

  setTab(tab) {
    set({ tab });
  },

  setSearch(search) {
    set({ search });
  },

  setCategoryFilter(categoryFilter) {
    set({ categoryFilter });
  },

  async openPack(packId) {
    set({ openPackId: packId, resourceTab: 'overview' });
    const activity = await vaultApi.noteRecentPack(packId);
    applyActivity(set, activity);
  },

  closePack() {
    set({ openPackId: null });
  },

  setResourceTab(resourceTab) {
    set({ resourceTab });
  },

  async togglePackFavorite(packId) {
    const next = !get().favoritePacks.includes(packId);
    const activity = await vaultApi.setPackFavorite(packId, next);
    applyActivity(set, activity);
  },

  async toggleItemFavorite(itemId) {
    const next = !get().favoriteItems.includes(itemId);
    const activity = await vaultApi.setItemFavorite(itemId, next);
    applyActivity(set, activity);
  },

  async recordDownload(pack) {
    const activity = await vaultApi.recordPackDownload(pack.id);
    applyActivity(set, activity);
  },

  async noteRecentPrompt(packId, promptId, title) {
    const activity = await vaultApi.noteRecentPrompt(packId, promptId, title);
    applyActivity(set, activity);
  },

  async noteRecentPreset(packId, presetId, name) {
    const activity = await vaultApi.noteRecentPreset(packId, presetId, name);
    applyActivity(set, activity);
  },

  async loadActivity() {
    const activity = await vaultApi.getMe();
    applyActivity(set, activity);
  },

  reset() {
    set({ ...INITIAL });
  },
}));
